import React, { useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, Send, Hand, Radio } from "lucide-react";

const STATUS_META = {
  not_synced: { label: "Not synced",   badge: "bg-[#F1F3F5] text-[#6C757D] border-[#E9ECEF]", icon: Radio },
  synced:     { label: "Synced",       badge: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  failed:     { label: "Sync failed",  badge: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  manual:     { label: "Manual entry", badge: "bg-blue-50 text-blue-700 border-blue-200", icon: Hand },
};

/**
 * Reusable Commissions Sync panel — shown on dealer / user / agency detail
 * views. Fires POST /admin/commissions-sync/{kind}/{id} and (for dealer only)
 * offers a "Mark manual" escape hatch when the push has failed.
 *
 * Props:
 *   kind: "dealer" | "agent" | "agency"
 *   id: record id
 *   status: not_synced | synced | failed | manual
 *   syncedAt, syncError, syncActor, syncTrigger
 *   onChange: () => void  (parent refresh)
 */
export default function CommissionsSyncPanel({ kind, id, status, syncedAt, syncError, syncActor, syncTrigger, onChange }) {
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [note, setNote] = useState("");
  const eff = STATUS_META[status || "not_synced"] || STATUS_META.not_synced;
  const Icon = eff.icon;

  const push = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/commissions-sync/${kind}/${id}`);
      if (data.ok) toast.success("Pushed to Commissions");
      else toast.error(data.error || "Push failed");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push failed");
    } finally {
      setBusy(false);
    }
  };

  const markManual = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/commissions-sync/${kind}/${id}/mark-manual`, { note: note || null });
      toast.success("Marked manually synced");
      setManualOpen(false);
      setNote("");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-sm border border-[#E9ECEF] bg-white p-4 flex flex-wrap items-center justify-between gap-3"
      data-testid={`commissions-sync-panel-${kind}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-sm grid place-items-center ${eff.badge}`}>
          <Icon size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
            Commissions platform
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-[0.18em] font-bold ${eff.badge}`}
              data-testid={`commissions-sync-status-${kind}`}
            >
              {eff.label}
            </span>
            {syncTrigger === "auto_countersign" && status === "synced" && (
              <span className="text-[10px] text-[#6C757D] italic">auto on countersign</span>
            )}
          </div>
          {syncedAt && (
            <div className="text-[11px] text-[#6C757D] mt-1">
              Last synced {new Date(syncedAt).toLocaleString()}
              {syncActor ? ` by ${syncActor}` : ""}
            </div>
          )}
          {status === "failed" && syncError && (
            <div
              className="text-[11px] text-red-700 mt-1 max-w-md break-words"
              data-testid={`commissions-sync-error-${kind}`}
            >
              {syncError}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={push}
          disabled={busy}
          className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold text-xs"
          data-testid={`btn-push-commissions-${kind}`}
        >
          {status === "failed" ? <RefreshCw size={13} className="mr-1.5" /> : <Send size={13} className="mr-1.5" />}
          {status === "not_synced" ? "Push to Commissions" : status === "failed" ? "Retry" : "Sync now"}
        </Button>
        {kind === "dealer" && status === "failed" && (
          <Button
            variant="outline"
            onClick={() => setManualOpen(true)}
            disabled={busy}
            className="h-9 rounded-sm text-xs border-[#0B132B]/20"
            data-testid="btn-mark-manual"
          >
            <Hand size={13} className="mr-1.5" /> Mark manual
          </Button>
        )}
      </div>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="mark-manual-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as manually synced</DialogTitle>
            <DialogDescription className="text-xs text-[#6C757D]">
              Use this after you've entered the dealer directly in the commissions platform. Clears the failure state so this dealer stops flagging in the queue.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Entered manually 2/12/26 — subagent flat 50"
              className="mt-2 h-10 rounded-sm"
              data-testid="mark-manual-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={markManual}
              disabled={busy}
              className="bg-[#0B132B] hover:bg-[#1C2541] text-white"
              data-testid="btn-confirm-mark-manual"
            >
              <Hand size={13} className="mr-1.5" /> Mark manual
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
